import { PrepareTransferInResponse } from "@/api/transactions";
import {
	getSPIframeEventType,
	getSPIframeHeight,
	getSoftpointIframeDomain,
} from "@/pages/Home/card/view/_components/SoftpointIframe/AddCreditCardIframe";
import Typography from "@/components/ui/typography";
import React, { useEffect, useRef, useState } from "react";

// sale_token renders a blank iframe (only its own spinner); it grows when an issuer
// challenge has to be shown (regular 3DS when DCAP doesn't apply).
const SALE_TOKEN_IFRAME_HEIGHT_PX = 100;
const IFRAME_LOAD_TIMEOUT_MS = 60_000;
// The iframe reports "iframeLoaded" before its own scripts (e.g. crypto-js, loaded async) are
// guaranteed to be ready; give them a moment before submitting.
const SUBMIT_DELAY_MS = 1_500;
// If the iframe never answers after submitting (it can fail silently), don't spin forever: the
// guest is told the payment is being confirmed (a charge, if any, is settled later).
const PAYMENT_RESULT_TIMEOUT_MS = 2 * 60_000;
// While an issuer challenge is on screen (regular 3DS when DCAP doesn't apply), the guest needs time.
const CHALLENGE_RESULT_TIMEOUT_MS = 10 * 60_000;

const SCRIPT_ID = "softPointIFrameScript";

// Non-Visa cards get no DCAP benefit from a Data Only call, so Kashu asks for a regular 3DS
// authentication instead (no challenge requested, the issuer may still show one) to keep the
// liability shift. Visa cards always run DCAP. Same as the gateway default, set explicitly.
const SEND_DCAP_3DS_AUTH_FALLBACK = 1;

type Props = {
	locationId: number;
	iframeAccessToken: string;
	payment: PrepareTransferInResponse["payment"];
	onApproved: (response: unknown) => void;
	onDeclined: (response: unknown) => void;
	// Submitted, but no outcome came back in time.
	onTimeout: () => void;
};

/**
 * Charges the card through the SoftPoint payment iframe in "sale_token" mode with
 * send_dcap: the iframe loads ThreatMetrix, runs 3DS device data collection and calls
 * process_card (Visa DCAP Data Only when eligible, regular 3DS otherwise).
 *
 * Same callback protocol as the Add Card iframe (softpoint-pay calls the global
 * onApprovedCallback / onDeclinedCallback / onErrorCallback, relayed as window messages),
 * so either component's callbacks work if both were loaded in this session.
 */
const TransferInPaymentIframe: React.FC<Props> = ({
	locationId,
	iframeAccessToken,
	payment,
	onApproved,
	onDeclined,
	onTimeout,
}) => {
	const [iframeHeight, setIframeHeight] = useState(SALE_TOKEN_IFRAME_HEIGHT_PX);
	// The iframe only grows past its blank size to show the issuer's challenge.
	const challengeShown = iframeHeight > SALE_TOKEN_IFRAME_HEIGHT_PX;
	const initializedRef = useRef(false);
	const submittedRef = useRef(false);
	const settledRef = useRef(false);
	const onApprovedRef = useRef(onApproved);
	const onDeclinedRef = useRef(onDeclined);
	const onTimeoutRef = useRef(onTimeout);
	onApprovedRef.current = onApproved;
	onDeclinedRef.current = onDeclined;
	onTimeoutRef.current = onTimeout;

	// softpoint-pay may relay the same result more than once (its listeners are added per
	// script load), so only the first outcome counts.
	const settleRef = useRef((approved: boolean | "timeout", data?: unknown) => {
		if (settledRef.current) return;
		settledRef.current = true;
		if (approved === "timeout") onTimeoutRef.current();
		else if (approved) onApprovedRef.current(data);
		else onDeclinedRef.current(data);
	});

	// Load softpoint-pay and create the iframe exactly once per mount (React StrictMode runs
	// effects twice in development; a second initPaymentForm would add a second iframe).
	useEffect(() => {
		if (initializedRef.current) return;
		initializedRef.current = true;

		const globals = window as unknown as Record<string, unknown>;
		const init = () => {
			const initPaymentForm = globals.initPaymentForm;
			if (typeof initPaymentForm !== "function") {
				settleRef.current(false, {
					error: { message: "Payment form unavailable" },
				});
				return;
			}
			initPaymentForm("iframeContent", {
				location_id: locationId,
				access_token: iframeAccessToken,
				payment_methods: ["sale_token"],
				amount: payment.amount,
				currency_iso: payment.currency_iso,
				process_type: payment.process_type,
			});
		};

		// Already loaded in this session (e.g. by Add Card): its top-level declarations can't be
		// evaluated twice, so reuse it instead of adding the script again.
		if (typeof globals.initPaymentForm === "function") {
			init();
			return;
		}

		const script = document.createElement("script");
		script.id = SCRIPT_ID;
		script.src = `${getSoftpointIframeDomain()}/api/softpoint-pay`;
		script.async = true;
		script.type = "text/JavaScript";
		script.onload = init;
		script.onerror = () =>
			settleRef.current(false, {
				error: { message: "Payment form did not load" },
			});
		document.body.appendChild(script);
	}, [iframeAccessToken, locationId, payment]);

	useEffect(() => {
		const settle = settleRef.current;
		const relay = (eventType: string) => (data: unknown) =>
			window.postMessage({ message: { eventType, data } }, "*");
		const globals = window as unknown as Record<string, unknown>;
		globals.onApprovedCallback = relay("spi_submit_approved");
		globals.onDeclinedCallback = relay("spi_submit_declined");
		globals.onErrorCallback = relay("spi_submit_error");

		let submitTimer: number | undefined;
		let resultTimer: number | undefined;
		const waitForResult = (ms: number) => {
			window.clearTimeout(resultTimer);
			resultTimer = window.setTimeout(() => settle("timeout"), ms);
		};

		const submit = () => {
			if (submittedRef.current) return;
			submittedRef.current = true;
			const submitPayment = globals.submitPayment;
			if (typeof submitPayment !== "function") {
				settle(false, { error: { message: "Payment form unavailable" } });
				return;
			}
			submitPayment({
				...payment,
				send_dcap_3ds_auth_fallback: SEND_DCAP_3DS_AUTH_FALLBACK,
				location_id: locationId,
				access_token: iframeAccessToken,
			});
			waitForResult(PAYMENT_RESULT_TIMEOUT_MS);
		};

		const onMessage = (event: MessageEvent) => {
			const iframeEventType = getSPIframeEventType(event);
			if (iframeEventType === "iframeLoaded") {
				if (!submittedRef.current && submitTimer === undefined) {
					submitTimer = window.setTimeout(submit, SUBMIT_DELAY_MS);
				}
				return;
			}
			if (iframeEventType === "sp_pay_increase_height") {
				const height = getSPIframeHeight(event);
				if (height) setIframeHeight(height);
				// The iframe grows to show an issuer challenge.
				if (
					submittedRef.current &&
					height &&
					height > SALE_TOKEN_IFRAME_HEIGHT_PX
				) {
					waitForResult(CHALLENGE_RESULT_TIMEOUT_MS);
				}
				return;
			}

			const message = (
				event.data as { message?: { eventType?: string; data?: unknown } }
			)?.message;
			if (message?.eventType === "spi_submit_approved") {
				settle(true, message.data);
			} else if (
				message?.eventType === "spi_submit_declined" ||
				message?.eventType === "spi_submit_error"
			) {
				settle(false, message.data);
			}
		};
		window.addEventListener("message", onMessage);

		const loadTimeout = window.setTimeout(() => {
			if (!submittedRef.current) {
				settle(false, { error: { message: "Payment form did not load" } });
			}
		}, IFRAME_LOAD_TIMEOUT_MS);

		return () => {
			window.clearTimeout(loadTimeout);
			window.clearTimeout(submitTimer);
			window.clearTimeout(resultTimer);
			window.removeEventListener("message", onMessage);
		};
	}, [iframeAccessToken, locationId, payment]);

	return (
		<div className="w-full max-w-md rounded-3xl bg-white p-4 shadow-lg">
			{challengeShown ? (
				<div
					className="mb-3 text-center"
					data-testid="transfer-in-challenge-header"
				>
					<Typography variant={"p"} className="font-semibold text-textDark">
						Verify this payment with your bank
					</Typography>
					<Typography variant={"p"} className="text-sm text-gray-500">
						Follow your bank's steps below. If you cancel, your card won't be
						charged.
					</Typography>
				</div>
			) : (
				<Typography variant={"p"} className="text-center text-textDark mb-2">
					Processing your payment securely...
				</Typography>
			)}
			<div
				className="w-full relative"
				id="iframeContent"
				data-testid="transfer-in-iframe"
				style={{ height: `${iframeHeight}px` }}
			>
				<style>{`
          #iframeContent iframe {
            height: 100% !important;
            width: 100% !important;
            border: 0;
            display: block;
          }
        `}</style>
			</div>
		</div>
	);
};

export default TransferInPaymentIframe;
